import { memo, type ReactNode } from "react";
import { Box, Card, CardContent, Skeleton, Stack } from "@mui/material";

const DOCUMENT_CARD_COUNT = 4;

const DocumentCardSkeleton = memo(function DocumentCardSkeleton({
  pageLayout,
}: {
  pageLayout: boolean;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        minHeight: pageLayout ? 196 : 180,
        display: "flex",
        flexDirection: "column",
        borderColor: "grey.200",
      }}
    >
      <CardContent
        sx={{
          p: 1.25,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 1.25 },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Skeleton variant="rounded" width={32} height={32} />
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="text" width="90%" height={16} />
          </Stack>
          <Skeleton variant="rounded" width={88} height={28} sx={{ borderRadius: 8 }} />
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Skeleton
          variant="rounded"
          height={pageLayout ? 36 : 40}
          sx={{ mt: 1, borderRadius: 1 }}
        />
      </CardContent>
    </Card>
  );
});

const DocumentsGridSkeleton = memo(function DocumentsGridSkeleton({
  compact,
  pageLayout,
}: {
  compact: boolean;
  pageLayout: boolean;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "repeat(2, minmax(0, 1fr))",
        },
        gap: compact ? 1.25 : 1.5,
      }}
    >
      {Array.from({ length: DOCUMENT_CARD_COUNT }).map((_, index) => (
        <DocumentCardSkeleton key={index} pageLayout={pageLayout} />
      ))}
    </Box>
  );
});

const SectionChromeSkeleton = memo(function SectionChromeSkeleton({
  children,
}: {
  children?: ReactNode;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          py: { xs: 1.5, md: 2 },
          px: { xs: 1.5, md: 2.5 },
          "&:last-child": { pb: { xs: 1.5, md: 2 } },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minHeight: 48 }}>
          <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2 }} />
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width={140} height={28} />
            <Skeleton variant="text" width="60%" height={18} />
          </Stack>
          <Skeleton variant="rounded" width={96} height={24} sx={{ borderRadius: 8 }} />
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
});

interface PatientSupportingDocumentsSkeletonProps {
  compact?: boolean;
  showHeader?: boolean;
  pageLayout?: boolean;
}

/** Placeholder 2×2 dos cards de documento — evita tela branca na hidratação. */
export const PatientSupportingDocumentsSkeleton = memo(
  function PatientSupportingDocumentsSkeleton({
    compact = false,
    showHeader = true,
    pageLayout = false,
  }: PatientSupportingDocumentsSkeletonProps) {
    const grid = (
      <DocumentsGridSkeleton compact={compact} pageLayout={pageLayout} />
    );

    return (
      <Stack spacing={compact || pageLayout ? 2 : 2}>
        {showHeader ? (
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 0.5, py: 0.5 }}>
            <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1.5 }} />
            <Stack spacing={0.5} sx={{ flex: 1 }}>
              <Skeleton variant="text" width={220} height={24} />
              <Skeleton variant="text" width="80%" height={18} />
            </Stack>
          </Stack>
        ) : null}

        {pageLayout ? (
          <Stack spacing={2.5}>
            <SectionChromeSkeleton>
              <Box sx={{ pt: 1.5 }}>
                <Skeleton
                  variant="rounded"
                  height={36}
                  sx={{ mb: 1.25, borderRadius: 1 }}
                />
                {grid}
              </Box>
            </SectionChromeSkeleton>
            <SectionChromeSkeleton />
          </Stack>
        ) : (
          grid
        )}
      </Stack>
    );
  },
);
